import Button from '@allesauto/core/button';
import InputField from '@allesauto/core/inputField';
import RadioButton from '@allesauto/core/radioButton';
import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import { PaymentType, SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { FinanceTypeEn } from '@allesauto/utils/consts';
import { createLinkForRouting } from '@allesauto/utils/funcs';
import { isWhiteLabel } from '@allesauto/utils/funcs.ts';
import { Routes_DE as Routes } from '@allesauto/utils/routes';
import { useFormik } from 'formik';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import ContactDealerForm from '@/components/forms/contactDealerForm/ContactDealerForm';
import { EuroSign } from '@/components/forms/FinancingForm/FinancingForm';
import OverlaySuccessCreditRating from '@/components/forms/overlaySuccess/OverlaySuccessCreditRating';

import { useCustomer } from '@/global/hooks/useCustomer';
import { usePreApprovalFormMutation } from '@/global/hooks/usePreApprovalFormMutation';
import {
  closeOverlay,
  openOverlay,
  setOverlayLoadingState,
} from '@/store/redux/actions/app';

export enum PreapprovalFormAnswers {
  Positive = 'Ja',
  Negative = 'Nein',
}

interface CreditAssessmentOverlayProps {
  setStatusPreapprovalPassed?: React.Dispatch<
    React.SetStateAction<undefined | boolean>
  >;
  vehicleCartItem: SchemaTypes.CartItem;
  setBackToPdpLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  setFormStatus?: React.Dispatch<
    React.SetStateAction<{ [key: string]: string } | undefined>
  >;
  statusPreapprovalPassed?: boolean | undefined;
}

const CreditAssessmentOverlay: React.FC<
  React.PropsWithChildren<CreditAssessmentOverlayProps>
> = ({
  setStatusPreapprovalPassed,
  vehicleCartItem,
  setBackToPdpLoading,
  setFormStatus,
  statusPreapprovalPassed,
}) => {
  const intl = useIntl();
  const router = useRouter();
  const dispatch = useDispatch();
  const whiteLabel = useWhiteLabel();
  const isCashRange =
    vehicleCartItem.payment.paymentType === PaymentType.CashRange;

  const { id, makeName, modelName } =
    vehicleCartItem.product as SchemaTypes.Vehicle;

  const { customer } = useCustomer();

  const { preApprovalFormRequest } = usePreApprovalFormMutation();

  const className = {
    inputContainerWithTopBorder:
      'flex justify-between items-center mb-[30px] pt-[24px] pb-[24px] border-t-[1px] border-b-[1px] border-grey30 md:pr-[16px] md:pl-[16px] sm:pr-[16px] sm:pl-[16px]',
    inputContainerWithNoTopBorder:
      'flex justify-between items-center mb-[30px] pb-[24px] border-b-[1px] border-grey30 md:pr-[16px] md:pl-[16px] sm:pr-[16px] sm:pl-[16px]',
    inputParagraphsWidth: 'w-[350px]  sm:text-p4',
    radioButtonsContainer:
      'flex sm:flex-col md:flex-row lg:flex justify-between w-[150px] sm:translate-x-[32px] md:translate-x-0',
    inputsCustomWidth: 'w-[150px]',
    radioButtonsCustomMarginBottom: 'mb-[16px]',
    contactDealerBackground: 'py-[60px] bg-[rgb(244,244,244)] rounded-md',
    contactDealerContent: 'flex flex-col max-w-[80%] mx-auto bg-white p-[40px]',
  };

  const pdpRoute = Routes.PDP_VEHICLES;
  const pdpParams = useMemo(() => {
    return {
      markName: makeName && `/${makeName}`,
      modelName: modelName && `/${modelName}`,
      id: id && `/${id}`,
    };
  }, [id, makeName, modelName]);

  const pdpLink = useMemo(
    () => createLinkForRouting(FinanceTypeEn.EMPTY, pdpRoute, pdpParams),
    [pdpRoute, pdpParams]
  );

  const handleBackToCheckoutProcess = () => {
    dispatch(closeOverlay());
  };

  const handleContactDealer = () => {
    dispatch(openOverlay(contactProps));
  };

  const handleBackToPdpWhenPreapprovalFails = () => {
    setBackToPdpLoading && setBackToPdpLoading(true);
    router.push(pdpLink);
  };

  useEffect(() => {
    const handleRouteChange = () => {
      setBackToPdpLoading && setBackToPdpLoading(false);
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router]);

  const getFinancingDetailBasedOnPaymentType = (
    vehicleCartItem: SchemaTypes.CartItem
  ) => {
    if (isCashRange) {
      return;
    }
    if (vehicleCartItem.product.__typename === 'Vehicle') {
      return vehicleCartItem.product.financingDetails.find(
        (detail) => detail.type === vehicleCartItem.payment.paymentType
      );
    }
    return undefined;
  };

  const financingDetailBasedOnPaymentType =
    getFinancingDetailBasedOnPaymentType(vehicleCartItem);

  const formik = useFormik({
    initialValues: {
      peopleInHousehold: '0',
      monthlyNetIncome: '0',
      existingMonthlyInstallmentsSum: '0',
      otherMonthlyExpensesSum: '0',
      hasNoInsolvencyProceedings: PreapprovalFormAnswers.Negative,
      hasNoNegativeCreditReports: PreapprovalFormAnswers.Negative,
      isNotABusinessOwner: PreapprovalFormAnswers.Negative,
      isVehiclePrivatelyUsed: PreapprovalFormAnswers.Negative,
    },

    onSubmit: async (data) => {
      dispatch(setOverlayLoadingState(true));
      // some key's in BE start with "hasNo" so when user in FE form selects "no" we are sending true, that's why we use !== in formData
      const formData: SchemaTypes.PreApprovalInput = {
        peopleInHousehold: parseInt(data.peopleInHousehold, 10),
        monthlyNetIncome: parseFloat(data.monthlyNetIncome),
        existingMonthlyInstallmentsSum: parseFloat(
          data.existingMonthlyInstallmentsSum
        ),
        otherMonthlyExpensesSum: parseFloat(data.otherMonthlyExpensesSum),
        hasNoInsolvencyProceedings:
          data.hasNoInsolvencyProceedings !== PreapprovalFormAnswers.Positive,
        hasNoNegativeCreditReports:
          data.hasNoNegativeCreditReports !== PreapprovalFormAnswers.Positive,
        isNotABusinessOwner:
          data.isNotABusinessOwner !== PreapprovalFormAnswers.Positive,
        isVehiclePrivatelyUsed:
          data.isVehiclePrivatelyUsed !== PreapprovalFormAnswers.Positive,
        bankingType: vehicleCartItem.payment.paymentType as PaymentType,
        finalInstallment:
          parseFloat(`${financingDetailBasedOnPaymentType?.finalPayment}`) || 0,
        purchasePrice: parseFloat(`${vehicleCartItem.payment.price}`),
        term: parseInt(`${financingDetailBasedOnPaymentType?.duration}`) || 0,
      };

      try {
        const result = await preApprovalFormRequest(id, formData);
        if (setFormStatus) {
          setFormStatus((prevStatus: any) => ({
            ...prevStatus,
            [(vehicleCartItem.product as SchemaTypes.Vehicle).id]: result
              .calculatePreApproval.isPreApproved
              ? 'Passed'
              : 'Failed',
          }));
        }

        dispatch(closeOverlay());

        if (result.calculatePreApproval.isPreApproved) {
          setTimeout(
            () =>
              dispatch(
                openOverlay({
                  children: (
                    <OverlaySuccessCreditRating
                      vehicleCartItem={vehicleCartItem}
                      onBtnClick={handleBackToCheckoutProcess}
                      setStatusPreapprovalPassed={setStatusPreapprovalPassed}
                      setBackToPdpLoading={setBackToPdpLoading}
                      setFormStatus={setFormStatus}
                      statusPreapprovalPassed={statusPreapprovalPassed}
                    />
                  ),
                  title: translate(
                    intl,
                    'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_OVERLAY_HEADING'
                  ),
                  creditAssessmentOverlay: true,
                  creditAssessmentSuccessOverlay: true,
                })
              ),
            100
          );
          if (setStatusPreapprovalPassed) {
            setStatusPreapprovalPassed(true);
          }
        } else {
          setTimeout(
            () =>
              dispatch(
                openOverlay({
                  children: (
                    <OverlaySuccessCreditRating
                      onBtnClick={handleContactDealer}
                      vehicleCartItem={vehicleCartItem}
                      creditRatingFailed
                      setStatusPreapprovalPassed={setStatusPreapprovalPassed}
                      setBackToPdpLoading={setBackToPdpLoading}
                      setFormStatus={setFormStatus}
                      statusPreapprovalPassed={statusPreapprovalPassed}
                    />
                  ),
                  title: translate(
                    intl,
                    'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_OVERLAY_HEADING'
                  ),
                  creditAssessmentOverlay: true,
                  creditAssessmentSuccessOverlay: true,
                })
              ),
            100
          );
          if (setStatusPreapprovalPassed) {
            setStatusPreapprovalPassed(false);
          }
        }
      } catch (e) {
        // the error is handled by the apollo errorHandler
        dispatch(setOverlayLoadingState(false));
      }
    },
    validationSchema: yup.object().shape({
      peopleInHousehold: yup
        .number()
        .required('Eingabe ist erforderlich')
        .typeError('Muss eine Zahl sein')
        .positive('Nur positive Zahlen'),
      monthlyNetIncome: yup
        .number()
        .required('Eingabe ist erforderlich')
        .typeError('Muss eine Zahl sein')
        .positive('Nur positive Zahlen'),
      existingMonthlyInstallmentsSum: yup
        .number()
        .required('Eingabe ist erforderlich')
        .typeError('Muss eine Zahl sein'),
      otherMonthlyExpensesSum: yup
        .number()
        .required('Eingabe ist erforderlich')
        .typeError('Muss eine Zahl sein'),
    }),
  });
  const contactProps = {
    children: (
      <ContactDealerForm
        dealerId={vehicleCartItem.dealer.id}
        vehicleSku={vehicleCartItem.sku}
        darkBg={false}
        carModel={
          vehicleCartItem.product.__typename === 'Vehicle'
            ? vehicleCartItem.product.modelName
            : ''
        }
        carManufacturer={
          vehicleCartItem.product.__typename === 'Vehicle'
            ? vehicleCartItem.product.makeName
            : ''
        }
        dealerName={vehicleCartItem.dealer.name || ''}
        onBackToSearchClick={handleBackToCheckoutProcess}
        contactPerson={{
          firstName: vehicleCartItem.dealer.contactPerson?.firstName || '',
          lastName: vehicleCartItem.dealer.contactPerson?.lastName || '',
          phone: vehicleCartItem.dealer.contactPerson?.phone || '',
        }}
        paymentType={vehicleCartItem.payment.paymentType || null}
        customerDetails={customer || undefined}
        financeCalculationResult={
          !isCashRange
            ? ({
                monthlyInstallment:
                  vehicleCartItem.payment.calculation?.monthlyInstallment,
                downPayment: vehicleCartItem.payment.calculation?.downPayment,
                duration: vehicleCartItem.payment.calculation?.duration,
                mileage: vehicleCartItem.payment.calculation?.mileage,
              } as SchemaTypes.FinanceCalculationResultInput) || undefined
            : undefined
        }
        vehiclePrice={vehicleCartItem.payment.price as number}
        preApprovalFormContactDealerOverlay
        statusPreapprovalPassed={statusPreapprovalPassed}
        handleBackToPdpWhenPreapprovalFails={
          handleBackToPdpWhenPreapprovalFails
        }
        submittedFormData={{
          ...formik.values,
          bankingType: vehicleCartItem.payment.paymentType as PaymentType,
          finalInstallment:
            parseFloat(`${financingDetailBasedOnPaymentType?.finalPayment}`) ||
            0,
          purchasePrice: parseFloat(`${vehicleCartItem.payment.price}`),
          term: parseInt(`${financingDetailBasedOnPaymentType?.duration}`) || 0,
        }}
      />
    ),
    title: translate(intl, 'ui.FORMS.CONTACT_DEALER.TITLE'),
  };

  return (
    <form>
      <div className="flex flex-col items-center justify-center max-w-[600px] mx-auto pb-40px w-full">
        <div className="h-full w-full">
          <div className={className.inputContainerWithTopBorder}>
            <p className={className.inputParagraphsWidth}>
              {translate(
                intl,
                isWhiteLabel(whiteLabel)
                  ? 'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_QUESTION_0_WL'
                  : 'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_QUESTION_0'
              )}
            </p>
            <InputField
              name="peopleInHousehold"
              customStyle={className.inputsCustomWidth}
              value={formik.values.peopleInHousehold}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              errorMessage={formik.errors.peopleInHousehold}
            />
          </div>

          <div className={className.inputContainerWithNoTopBorder}>
            <p className={className.inputParagraphsWidth}>
              {translate(
                intl,
                'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_QUESTION_1'
              )}
            </p>
            <InputField
              name="monthlyNetIncome"
              customStyle={className.inputsCustomWidth}
              icon={<EuroSign />}
              value={formik.values.monthlyNetIncome}
              onChange={formik.handleChange}
              errorMessage={formik.errors.monthlyNetIncome}
            />
          </div>

          <div className={className.inputContainerWithNoTopBorder}>
            <p className={className.inputParagraphsWidth}>
              {translate(
                intl,
                'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_QUESTION_2'
              )}
            </p>
            <InputField
              name="existingMonthlyInstallmentsSum"
              customStyle={className.inputsCustomWidth}
              icon={<EuroSign />}
              value={formik.values.existingMonthlyInstallmentsSum}
              onChange={formik.handleChange}
              errorMessage={formik.errors.existingMonthlyInstallmentsSum}
            />
          </div>

          <div className={className.inputContainerWithNoTopBorder}>
            <p className={className.inputParagraphsWidth}>
              {translate(
                intl,
                'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_QUESTION_3'
              )}
            </p>
            <InputField
              name="otherMonthlyExpensesSum"
              customStyle={className.inputsCustomWidth}
              icon={<EuroSign />}
              value={formik.values.otherMonthlyExpensesSum}
              onChange={formik.handleChange}
              errorMessage={formik.errors.otherMonthlyExpensesSum}
            />
          </div>

          <div className={className.inputContainerWithNoTopBorder}>
            <p className={className.inputParagraphsWidth}>
              {translate(
                intl,
                isWhiteLabel(whiteLabel)
                  ? 'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_QUESTION_4_WL'
                  : 'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_QUESTION_4'
              )}
            </p>
            <div className={className.radioButtonsContainer}>
              <RadioButton
                name="hasNoInsolvencyProceedings"
                className={className.radioButtonsCustomMarginBottom}
                label={translate(
                  intl,
                  'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_RADIO_LABEL_NO'
                )}
                onChange={formik.handleChange}
                formik={formik}
                checked={
                  formik.values.hasNoInsolvencyProceedings ===
                  PreapprovalFormAnswers.Negative
                }
                value={PreapprovalFormAnswers.Negative}
              />
              <RadioButton
                name="hasNoInsolvencyProceedings"
                className={className.radioButtonsCustomMarginBottom}
                label={translate(
                  intl,
                  'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_RADIO_LABEL_YES'
                )}
                onChange={formik.handleChange}
                formik={formik}
                checked={
                  formik.values.hasNoInsolvencyProceedings ===
                  PreapprovalFormAnswers.Positive
                }
                value={PreapprovalFormAnswers.Positive}
              />
            </div>
          </div>

          <div className={className.inputContainerWithNoTopBorder}>
            <p className={className.inputParagraphsWidth}>
              {translate(
                intl,
                isWhiteLabel(whiteLabel)
                  ? 'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_QUESTION_5_WL'
                  : 'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_QUESTION_5'
              )}
            </p>
            <div className={className.radioButtonsContainer}>
              <RadioButton
                name="hasNoNegativeCreditReports"
                className={className.radioButtonsCustomMarginBottom}
                label={translate(
                  intl,
                  'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_RADIO_LABEL_NO'
                )}
                onChange={formik.handleChange}
                formik={formik}
                checked={
                  formik.values.hasNoNegativeCreditReports ===
                  PreapprovalFormAnswers.Negative
                }
                value={PreapprovalFormAnswers.Negative}
              />
              <RadioButton
                name="hasNoNegativeCreditReports"
                className={className.radioButtonsCustomMarginBottom}
                label={translate(
                  intl,
                  'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_RADIO_LABEL_YES'
                )}
                onChange={formik.handleChange}
                formik={formik}
                checked={
                  formik.values.hasNoNegativeCreditReports ===
                  PreapprovalFormAnswers.Positive
                }
                value={PreapprovalFormAnswers.Positive}
              />
            </div>
          </div>

          <div className={className.inputContainerWithNoTopBorder}>
            <p className={className.inputParagraphsWidth}>
              {translate(
                intl,
                isWhiteLabel(whiteLabel)
                  ? 'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_QUESTION_6_WL'
                  : 'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_QUESTION_6'
              )}
            </p>
            <div className={className.radioButtonsContainer}>
              <RadioButton
                name="isNotABusinessOwner"
                className={className.radioButtonsCustomMarginBottom}
                label={translate(
                  intl,
                  'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_RADIO_LABEL_NO'
                )}
                onChange={formik.handleChange}
                formik={formik}
                checked={
                  formik.values.isNotABusinessOwner ===
                  PreapprovalFormAnswers.Positive
                }
                value={PreapprovalFormAnswers.Negative}
              />
              <RadioButton
                name="isNotABusinessOwner"
                className={className.radioButtonsCustomMarginBottom}
                label={translate(
                  intl,
                  'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_RADIO_LABEL_YES'
                )}
                onChange={formik.handleChange}
                formik={formik}
                checked={
                  formik.values.isNotABusinessOwner ===
                  PreapprovalFormAnswers.Positive
                }
                value={PreapprovalFormAnswers.Positive}
              />
            </div>
          </div>

          <div className={className.inputContainerWithNoTopBorder}>
            <p className={className.inputParagraphsWidth}>
              {translate(
                intl,
                isWhiteLabel(whiteLabel)
                  ? 'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_QUESTION_7_WL'
                  : 'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_QUESTION_7'
              )}
            </p>
            <div className={className.radioButtonsContainer}>
              <RadioButton
                name="isVehiclePrivatelyUsed"
                className={className.radioButtonsCustomMarginBottom}
                label={translate(
                  intl,
                  'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_RADIO_LABEL_NO'
                )}
                onChange={formik.handleChange}
                formik={formik}
                checked={
                  formik.values.isVehiclePrivatelyUsed ===
                  PreapprovalFormAnswers.Positive
                }
                value={PreapprovalFormAnswers.Negative}
              />
              <RadioButton
                name="isVehiclePrivatelyUsed"
                className={className.radioButtonsCustomMarginBottom}
                label={translate(
                  intl,
                  'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_RADIO_LABEL_YES'
                )}
                onChange={formik.handleChange}
                formik={formik}
                checked={
                  formik.values.isVehiclePrivatelyUsed ===
                  PreapprovalFormAnswers.Positive
                }
                value={PreapprovalFormAnswers.Positive}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={className.contactDealerBackground}>
        <div className={className.contactDealerContent}>
          <p className="font-weight-bold mb-[20px]">
            {translate(intl, 'ui.PAYMENT_OPTIONS.NO_DOCUMENTS_AT_HAND')}
          </p>
          <p className="mb-[20px]">
            {translate(
              intl,
              isWhiteLabel(whiteLabel)
                ? 'ui.PAYMENT_OPTIONS.SEND_PERSONAL_REQUEST_WL'
                : 'ui.PAYMENT_OPTIONS.SEND_PERSONAL_REQUEST'
            )}
          </p>
          <Button
            customClassName={'bg-transparent md:w-1/2 border-purple90'}
            textColor="purple90"
            text={translate(
              intl,
              'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_CONTACT_DEALER'
            )}
            disabledHover
            onClick={() => {
              dispatch(openOverlay(contactProps));
            }}
          />
        </div>
      </div>
      <Button
        text={translate(intl, 'ui.PAYMENT_OPTIONS.CREDIT_RATING_BUTTON_TEXT')}
        fullWidth
        onClick={formik.handleSubmit}
        disabled={!formik.isValid}
        customClassName={'h-[100px] rounded-none'}
      />
    </form>
  );
};

export default CreditAssessmentOverlay;
